import React, {createContext, useContext, useEffect, useState} from 'react';
import axios from 'axios';
import {
  urlActivation,
  urlChangePass,
  urlRestorePass,
  urlSignIn,
  urlSignOut,
  urlSignUp,
  wapiUrl,
} from './services/baseUrl';
import {LOGIN_ROUTE, system} from './services/objects';
import {isEmptyObject} from 'services/SecondaryMethods/typeUtils';
import {Spinner} from 'components/spinner';
import useHistory from 'components/useHistory';
import {routeFromHash} from 'utilsOld/location';
import {IUser} from 'services/interfaces/global-interfaces';
import useInitAxios from './useInitAxios';

export interface AuthContextInterface {
  isAuth: boolean;
  user: IUser | null;
  signIn: (login: string, password: string) => Promise<void>;
  activationFunction: (code: string) => Promise<void>;
  restorePassword: (email: string) => Promise<void>;
  changePassword: (credentials: {ActivationCode: string; Password: string}) => Promise<void>;
  signOut: () => void;
  signUp: (args: {Login: string; Name: string; EMail: string; Password: string}) => Promise<void>;
  checkLogin: () => Promise<{ResponseCode: '000' | '401'}>;
}

const AuthContext = createContext<AuthContextInterface | null>(null);
const useAuth = (): Partial<AuthContextInterface> => useContext(AuthContext) || {};

const clearStorage = () => {
  localStorage.removeItem(system.current_user);
};

const AuthProvider: React.FC<{children?: React.ReactNode}> = ({children}) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  const signIn = async (login: string, password: string) => {
    const options = {
      method: 'POST',
      url: urlSignIn,
      contentType: 'Application/jsoncharset=UTF-8',
      data: {login, password},
    } as const;

    const {data} = await axios(options);
    if (data.ResponseCode !== '000') {
      throw new Error(data.ResponseText);
    }
    localStorage.setItem(system.current_user, JSON.stringify(data.Response));
    setUser(data.Response);

    const route = routeFromHash(window.location.hash);
    const returnRoute = localStorage.getItem(system.ReturnUrl);
    localStorage.removeItem(system.ReturnUrl);

    if (route === LOGIN_ROUTE) {
      history.push(returnRoute || '/');
    }
  };

  const checkLogin = async () => {
    const options = {
      method: 'GET',
      url: `${wapiUrl}/Sys_Login/CheckLogin`,
    } as const;

    const {data} = await axios(options);
    return data;
  };

  const signUp = (creds: {Login: string; Name: string; EMail: string; Password: string}) => {
    clearStorage();
    const headerDefault = {};

    const option = {
      method: 'POST',
      headers: headerDefault,
      url: urlSignUp,
      data: {
        [system.REQUEST]: {
          [system.SYS_LOGIN]: creds,
        },
      },
    } as const;

    return axios(option).then(({data}) => {
      if (data.ResponseCode !== '000') {
        return Promise.reject(new Error(data.ResponseText));
      }
      return Promise.resolve(data.Response);
    });
  };

  //  user activation
  const activationFunction = (code: string) => {
    clearStorage();

    const options = {
      method: 'POST',
      url: urlActivation,
      contentType: 'Application/jsoncharset=UTF-8',
      data: {[system.REQUEST]: {[system.SYS_LOGIN]: {ActivationCode: code}}},
    } as const;

    return axios(options).then(({data}) => {
      if (data.ResponseCode !== '000') {
        return Promise.reject(new Error(data.ResponseText));
      }
      return Promise.resolve(data);
    });
  };

  const restorePassword = (email: string) => {
    clearStorage();
    const options = {
      method: 'POST',
      url: urlRestorePass,
      contentType: 'Application/jsoncharset=UTF-8',
      data: {[system.REQUEST]: {[system.SYS_LOGIN]: {EMail: email}}},
    } as const;

    return axios(options).then(({data}) => {
      if (data.ResponseCode !== '000') {
        return Promise.reject(new Error(data.ResponseText));
      }
      return Promise.resolve(data.Response);
    });
  };
  //  user changePassword
  const changePassword = (credentials: {ActivationCode: string; Password: string}) => {
    clearStorage();

    const options = {
      method: 'POST',
      url: urlChangePass,
      contentType: 'Application/jsoncharset=UTF-8',
      data: {[system.REQUEST]: {[system.SYS_LOGIN]: credentials}},
    } as const;

    return axios(options).then(({data}) => {
      if (data.ResponseCode !== '000') {
        return Promise.reject(new Error(data.ResponseText));
      }
      return Promise.resolve(data.Response);
    });
  };

  const signOut = () => {
    const options = {
      method: 'POST',
      url: urlSignOut,
    } as const;

    axios(options);
    clearStorage();
    setUser(null);
  };

  useInitAxios({signOut});

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem(system.current_user) || '{}') as IUser;

    if (isEmptyObject(currentUser)) {
      return setLoading(false);
    }

    (async function () {
      let resp: any = {};
      try {
        resp = await checkLogin();
      } catch {
        return setLoading(false);
      }

      if (resp.ResponseCode === '000') {
        setUser(currentUser);
      }

      setLoading(false);
    })();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuth: !!user,
        user,
        signIn,
        signUp,
        signOut,
        checkLogin,
        changePassword,
        restorePassword,
        activationFunction,
      }}
    >
      {loading ? <Spinner /> : children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
export {useAuth};
